import { filterClasses } from "@swivl-mono/helpers";
import { Table, flexRender } from "@tanstack/react-table";
import { useCallback, useEffect, useRef } from "react";
import { BiSortDown, BiSortUp } from "react-icons/bi";

import { DataTableVariant, dataTableVariantClassNames } from "./DataTable";

const DataTableHeader = ({
  table,
  variant,
  onHeaderWidthsChange
}: {
  table: Table<any>;
  variant: DataTableVariant;
  onHeaderWidthsChange?: (widths: Record<string, number>) => void;
}) => {
  const headerRefs = useRef<Record<string, HTMLElement | null>>({});
  const hasMeasured = useRef(false);

  const measureHeaders = useCallback(() => {
    if (!onHeaderWidthsChange || hasMeasured.current) return;

    // measure all header widths after render
    const widths: Record<string, number> = {};
    Object.entries(headerRefs.current).forEach(([id, element]) => {
      if (element) {
        // skip columns that already have explicit sizes- like select col
        const column = table.getAllColumns().find((col) => col.id === id);
        if (column?.columnDef.minSize) {
          return;
        }

        const contentWidth = element.offsetWidth;
        // padding for resize handler and some extra buffer
        const extraPadding = 8;
        widths[id] = contentWidth + extraPadding;
      }
    });

    if (Object.keys(widths).length > 0) {
      onHeaderWidthsChange(widths);
      hasMeasured.current = true;
    }
  }, [onHeaderWidthsChange, table]);

  useEffect(() => {
    // measure on initial render
    measureHeaders();
  }, [measureHeaders]);

  return (
    <thead className={filterClasses("sticky top-0 z-10 h-10 text-left", dataTableVariantClassNames[variant].header)}>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr
          className={filterClasses("font-mono text-xs font-bold", variant !== DataTableVariant.SECONDARY && "shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]")}
          key={headerGroup.id}
        >
          {headerGroup.headers.map((header, index) => (
            <th className="h-10" key={header.id} colSpan={header.colSpan} style={{ width: `${header.getSize()}px` }}>
              <div className="flex h-full items-center border-b border-grey-200">
                {header.id === "select" ? (
                  <div
                    ref={(el) => {
                      headerRefs.current[header.id] = el;
                    }}
                    className="flex size-full items-center p-3 text-left"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                ) : header.column.getCanSort() ? (
                  <button
                    ref={(el) => {
                      headerRefs.current[header.id] = el;
                    }}
                    className={filterClasses(
                      "flex size-full cursor-pointer items-center gap-1 px-3 py-2 text-left",
                      "transition-colors duration-150 ease-in-out hover:bg-grey-300"
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.column.getIsSorted() ? (
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    ) : (
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    )}
                    {header.column.getIsSorted() === "asc" ? (
                      <BiSortUp className="size-4 text-orange-500" />
                    ) : header.column.getIsSorted() === "desc" ? (
                      <BiSortDown className="size-4 text-orange-500" />
                    ) : (
                      <BiSortDown className="size-4 text-black" />
                    )}
                  </button>
                ) : (
                  <div
                    ref={(el) => {
                      headerRefs.current[header.id] = el;
                    }}
                    className="flex size-full items-center px-3 py-2 text-left"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                )}

                {/* divider / col resizer */}
                {index < headerGroup.headers.length - 1 && (
                  <div
                    aria-orientation="vertical"
                    className="relative h-4 w-px cursor-col-resize bg-[#d9d9d9]"
                    onDoubleClick={() => header.column.resetSize()}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    role="separator"
                  >
                    {/* transparent hit area */}
                    <div className="absolute inset-y-0 -left-1 h-full w-2 cursor-col-resize" />
                  </div>
                )}
              </div>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
};

export { DataTableHeader };
